import { useCallback, useMemo } from 'react';
import { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Form, FormField } from 'core/components';
import { InputType, NotificationPosition } from 'core/enums';
import { FormProvider, useNotificationContext, useSelectionCountContext } from 'core/states';
import { useJobPartActions } from 'jobs/hooks';
import { IJobPart } from 'jobs/types';
import { PartNumberHelper } from 'parts/helpers';
import { ProjectFormField } from 'projects/components';

import { mapAddToJobData } from './mappers';
import { addToJobSchema } from './schema';
import { SeeJobButton } from './SeeJobButton';
import { IAddToJobFormData } from './types';

interface IProps {
  isOpen?: boolean;
  onClose?: () => void;
  jobParts: IJobPart[];
}

export const AddToJob = ({ isOpen, onClose, jobParts }: IProps) => {
  const { t } = useTranslation();

  const { showSuccessNotification, clearNotification } = useNotificationContext();
  const { selectionCount } = useSelectionCountContext();

  const { addPartsToJob } = useJobPartActions({ selectionCount });

  const defaultValues = useMemo(
    () => ({
      project: null,
      jobName: '',
    }),
    []
  );

  const handleOnClose = useCallback(
    (reset: UseFormReset<IAddToJobFormData>) => {
      reset();
      clearNotification();
      onClose();
    },
    [clearNotification, onClose]
  );

  const onSubmit = useCallback(
    (reset: UseFormReset<IAddToJobFormData>) => async (data: IAddToJobFormData) => {
      const {
        success,
        data: { bomId },
      } = await addPartsToJob(mapAddToJobData(data, jobParts));

      if (!success) {
        return;
      }

      handleOnClose(reset);

      showSuccessNotification(
        {
          message: t('messageJobPartsAddToJobSuccess', {
            count: selectionCount,
            jobName: data.jobName,
          }),
          buttons: [<SeeJobButton key="seeJob" jobId={bomId} />],
          disableAutoClose: true,
        },
        NotificationPosition.TOP
      );
    },
    [jobParts, addPartsToJob, showSuccessNotification, t, handleOnClose, selectionCount]
  );

  return (
    <FormProvider schema={addToJobSchema()} defaultValues={defaultValues} resetDefaultValues>
      <Form
        title={t('titleAddToJob')}
        subtitle={t('subtitleJobPartsAddToJob', {
          number:
            PartNumberHelper.toLabel(jobParts[0].partArr?.number) || jobParts[0].componentName,
          count: selectionCount,
        })}
        isOpen={isOpen}
        onClose={handleOnClose}
        submitBtnLabel={t('buttonLabelAddToJob')}
        onSubmit={onSubmit}
      >
        <ProjectFormField />

        <FormField
          name="jobName"
          type={InputType.TEXT_INPUT}
          inputProps={{
            label: t('labelJobName'),
            required: true,
          }}
        />
      </Form>
    </FormProvider>
  );
};
